import { KEY } from '../../stores/keys'
import { getLong, logicalKeys, type Storage } from '../../platform/storage'
import { loadProfile } from '../../stores/profile'
import { loadWindows } from '../../stores/history'
import { loadAllNotes, type NoteTag } from '../../stores/notes'
import { outboxOf, type Outbox } from '../../stores/outbox'
import { loadEvents, windowEnds, type EventKind } from '../../stores/events'
import type { Phase } from '../../core/types'
import { currentKid } from '../../stores/children'

/**
 * Посылка: что устройство рассказывает о себе серверу.
 *
 * Обмен идёт целиком и не боится повторов: у ребёнка есть постоянный номер, у заметок —
 * свои, у окна — момент начала. Побеждает та сторона, где запись свежее.
 */

export const CHILD_ID_KEY = 'moonwake.child_id'

/**
 * Номер малыша для обмена — тот, под которым его данные лежат на устройстве. Его выдал
 * сервер при создании (28.10.2026); телефон номеров не придумывает. Нет малыша — нет номера,
 * и отправлять нечего.
 */
export function childClientId(): string | null {
  return currentKid.value
}

export interface ChildPayload {
  clientId: string
  /** Анкету завели кнопкой «Добавить второго малыша»: это точно другой ребёнок. */
  separate?: boolean
  /**
   * Как малыша зовёт сервер. У новых малышей совпадает с `clientId`: сервер принимает номер
   * устройства как свой. Отличается только у заведённых до 28.10.2026 — тогда устройство
   * записывает его рядом в список малышей, а ящик не переименовывает.
   */
  serverId?: string | null
  name: string
  birthMs: number
  gender: 'BOY' | 'GIRL'
  gasVariant: boolean
  birthWeightGrams?: number | null
  customPhases: Record<string, Phase[]>
  /** Добавленные ВБ 2…N по программам: `[{ name, phases }]` (27.10.2026). Старый сервер поле не знает — и не присылает. */
  wakeSchedules?: Record<string, unknown[]>
  updatedAtMs: number
}

export interface WindowPayload {
  startedAtMs: number
  endedAtMs: number | null
  programId: string | null
}

export interface NotePayload {
  clientId: number
  day: number
  atMs: number
  tag: NoteTag
  text: string
  deleted: boolean
  updatedAtMs: number
}

export interface EventPayload {
  clientId: number
  kind: EventKind
  atMs: number
  windowMs: number
  phaseIndex: number | null
  title: string | null
  plannedMinutes: number | null
  /** Чем кончилось окно: уснул или сбросили. */
  reason: string | null
  grams: number | null
  /** Окно бодрствования вписали руками, а не отсчитал таймер. */
  manual?: boolean
  deleted?: boolean
  /** Ручная запись заменяет окно таймера с этим началом (правка времени, 20.10.2026). */
  replacesMs?: number | null
  /** Где спал и комментарий родителя. */
  place?: string | null
  note?: string | null
  /** Комментарий к бодрствованию: отдельный от комментария ко сну (21.10.2026). */
  wakeNote?: string | null
}

export interface SyncAnswer {
  child: ChildPayload | null
  /** Под каким номером устройство прислало этого малыша: сервер мог назвать запись иначе. */
  requestedClientId?: string
  /** Показывали ли человеку тур: признак аккаунта, приходит с любым ответом обмена. */
  tourSeen?: boolean
  /** Язык аккаунта и когда его выбрали; `lang: null` — человек ещё не выбирал. */
  locale?: { lang: string | null; updatedAtMs: number }
  /** У аккаунта нет согласия под текущей редакцией политики — показать «догоняющий» экран. */
  needsConsent?: boolean
  windows: WindowPayload[]
  notes: NotePayload[]
  events: EventPayload[]
  serverNow: number
}

/** Все правки «Моего дня», как они лежат в хранилище: ключ — id программы. */
async function collectCustomPhases(storage: Storage): Promise<Record<string, Phase[]>> {
  const out: Record<string, Phase[]> = {}
  for (const key of logicalKeys(await storage.keys())) {
    if (!key.startsWith('custom_phases_')) continue
    const raw = await getLong(storage, key)
    if (raw === null) continue
    try {
      const parsed = JSON.parse(raw)
      if (Array.isArray(parsed)) out[key.slice('custom_phases_'.length)] = parsed as Phase[]
    } catch {
      // повреждённая запись не должна мешать обмену
    }
  }
  return out
}

/** Списки ВБ по программам — как лежат, без разбора: сервер хранит их как есть. */
async function collectSchedules(storage: Storage): Promise<Record<string, unknown[]>> {
  const out: Record<string, unknown[]> = {}
  for (const key of logicalKeys(await storage.keys())) {
    if (!key.startsWith('wake_schedules_') || key.startsWith('wake_schedules_updated_at_')) continue
    const raw = await getLong(storage, key)
    if (raw === null) continue
    try {
      const parsed = JSON.parse(raw)
      if (Array.isArray(parsed) && parsed.length > 0) out[key.slice('wake_schedules_'.length)] = parsed as unknown[]
    } catch {
      // повреждённая запись не должна мешать обмену
    }
  }
  return out
}

export interface KidPayload {
  child: ChildPayload
  windows: WindowPayload[]
  notes: NotePayload[]
  events: EventPayload[]
}

/**
 * Посылка на сервер: всё, что есть, или только помеченное в исходящей корзине (после первого
 * полного обмена). Вместе с ней — список того, что в неё вошло: по нему после удачного обмена
 * снимаются метки, а то, что добавилось за время обмена, остаётся до следующего.
 */
export async function outgoing(storage: Storage, known?: string): Promise<{ payload: KidPayload; sent: { events: number[]; windows: number[] }; sentAt: number } | null> {
  const box = await outboxOf(storage)
  const sentAt = Date.now()
  const payload = await localSnapshot(storage, known, box)
  if (payload === null) return null
  return { payload, sent: { events: payload.events.map((e) => e.clientId), windows: payload.windows.map((w) => w.startedAtMs) }, sentAt }
}

/** Что есть на устройстве прямо сейчас; с [box] — только помеченное к отправке. */
export async function localSnapshot(storage: Storage, known?: string, box?: Outbox): Promise<KidPayload | null> {
  const profile = await loadProfile(storage)
  // Номер спрашиваем только когда есть о ком говорить: иначе `childClientId()` заводил
  // и запоминал номер на устройстве, где малыша нет, и этот номер потом всплывал
  // как «тот самый» при переезде со старой раскладки (случай 12.10.2026)
  if (profile === null) return null
  const clientId = known ?? childClientId()
  if (clientId === null) return null

  const updatedAtMs = Number((await storage.get(KEY.profileUpdatedAt)) ?? 0)
  const все = await loadEvents(storage)
  const частями = box !== undefined && box.incremental
  const events = частями ? все.filter((e) => box.events.has(e.id)) : все
  // Конец окна лежит записью, а не в списке начал: формат `windows_<месяц>` читают
  // часы и виджет Android, менять его нельзя.
  // Концы — по всему журналу: конец окна мог быть записан раньше, чем его начало попало в посылку
  const ends = windowEnds(все)
  const starts = (await loadWindows(storage)).filter((s) => !частями || box.windows.has(s))
  const notes = (await loadAllNotes(storage)).filter((n) => !частями || (n.updatedAt ?? 0) >= box.sentAt)
  return {
    child: {
      clientId,
      separate: (await storage.get(KEY.separateChild)) !== null,
      name: profile.name,
      birthMs: profile.birthMillis,
      gender: profile.gender,
      gasVariant: profile.gasVariant,
      birthWeightGrams: profile.birthWeightGrams ?? null,
      customPhases: await collectCustomPhases(storage),
      wakeSchedules: await collectSchedules(storage),
      updatedAtMs,
    },
    windows: starts.map((startedAtMs) => ({
      startedAtMs,
      endedAtMs: ends.get(startedAtMs) ?? null,
      programId: null,
    })),
    events: events.map((e) => ({
      clientId: e.id,
      kind: e.kind,
      atMs: e.at,
      windowMs: e.window,
      // Отрицательный индекс — фаза без окна, такой записи сервер не примет; шлём без номера
      phaseIndex: e.index === undefined || e.index < 0 ? null : e.index,
      title: e.title ?? null,
      plannedMinutes: e.planned ?? null,
      reason: e.reason ?? null,
      grams: e.grams ?? null,
      manual: e.manual === true,
      deleted: e.deleted === true,
      replacesMs: e.replaces ?? null,
      place: e.place ?? null,
      note: e.note ?? null,
      wakeNote: e.wakeNote ?? null,
    })),
    notes: notes.map((note) => ({
      clientId: note.id,
      day: note.day,
      atMs: note.time,
      tag: note.tag,
      text: note.text,
      deleted: note.deleted === true,
      // Сервер сравнивает по этой метке: без неё правка текста выглядела как «ничего нового»
      updatedAtMs: note.updatedAt ?? note.time,
    })),
  }
}
